import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  increment,
  arrayUnion,
  serverTimestamp,
  Timestamp,
  runTransaction,
  deleteField,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../firebase';
import { User } from 'firebase/auth';
import { Element as ElementType, Goal } from '@/types';
import { getRandomWord } from '@/lib/api';
import { v4 as uuidv4 } from 'uuid';
import { initialElements } from '@/lib/constants';

// Import additional Firestore functions
import {
  connectFirestoreEmulator,
  initializeFirestore,
  CACHE_SIZE_UNLIMITED,
} from 'firebase/firestore';

// Import retry utilities
import { retryFirestoreOperation } from './retryUtils';

// Check if we're in development mode
const isDevelopment = import.meta.env.DEV;

// Get the current hostname to handle custom domains
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

// Import auth functions
import { getAuth, onAuthStateChanged } from 'firebase/auth';
const auth = getAuth(app);

// Initialize Firestore with explicit settings for better performance and offline support
// Specify the correct database ID to fix WebChannelConnection RPC errors
export const db = initializeFirestore(
  app,
  {
    cacheSizeBytes: CACHE_SIZE_UNLIMITED,
    experimentalForceLongPolling: true, // Use long polling instead of WebSockets
    ignoreUndefinedProperties: true, // Ignore undefined fields in documents
  },
  'vibecraft'
); // Explicitly use the 'vibecraft' database ID

// Log the current hostname for debugging

// Add a listener for auth state changes to handle token refreshes
// This is crucial for fixing CORS issues with custom domains

onAuthStateChanged(auth, (user) => {
  if (user) {
    // Force token refresh to avoid CORS issues
    user
      .getIdToken(true)
      .then(() => {})
      .catch((error) => {});
  } else {
  }
});

// Connect to emulator in development mode
if (isDevelopment) {
  try {
    // Use localhost emulator if available
    connectFirestoreEmulator(db, 'localhost', 8080);
  } catch (error) {}
}

// Disable persistence for now to fix connectivity issues
// We'll use local state management instead
/*
try {
  enableIndexedDbPersistence(db)
    .then(() => {

    })
    .catch((err) => {
      if (err.code === 'failed-precondition') {
        // Multiple tabs open, persistence can only be enabled in one tab at a time

      } else if (err.code === 'unimplemented') {
        // The current browser does not support persistence

      } else {

      }
    });
} catch (error) {

}
*/

// Add some debug logging

// User data interface
export interface UserData {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  actionsRemaining: number;
  isPremium: boolean;
  createdAt: Timestamp;
  lastLogin: Timestamp;
  gameState?: {
    elements: ElementType[];
    favorites: ElementType[];
    discoveries: ElementType[];
    goals: Goal[];
    points: number;
    skipCount: number;
    keyCount?: number;
    zoomLevel?: number;
    wordHistory?: {
      Item: string[];
      Location: string[];
      Character: string[];
    };
  };
  purchases?: {
    date: Timestamp;
    amount: number;
    actionsAdded: number;
    paymentId: string;
  }[];
}

// No longer tracking anonymous users

// Create or update user document with improved error handling
export async function createOrUpdateUser(user: User): Promise<UserData> {
  try {
    return await retryFirestoreOperation(async () => {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        // Generate random words for initial goals
        const itemWord = await getRandomWord('Item');
        const locationWord = await getRandomWord('Location');
        const characterWord = await getRandomWord('Character');

        // Create new user document with gameState
        const userData: Partial<UserData> = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          actionsRemaining: 10,
          isPremium: false,
          createdAt: serverTimestamp() as Timestamp,
          lastLogin: serverTimestamp() as Timestamp,
          gameState: {
            elements: [
              { id: 'water', name: 'Water', type: 'water' },
              { id: 'fire', name: 'Fire', type: 'fire' },
              { id: 'dirt', name: 'Dirt', type: 'dirt' },
              { id: 'air', name: 'Air', type: 'air' },
            ],
            favorites: [],
            discoveries: [],
            goals: [
              { id: uuidv4(), type: 'Item', word: itemWord, matched: false },
              { id: uuidv4(), type: 'Location', word: locationWord, matched: false },
              { id: uuidv4(), type: 'Character', word: characterWord, matched: false },
            ],
            points: 0,
            skipCount: 0,
          },
        };

        await setDoc(userRef, userData);

        return { ...userData, createdAt: Timestamp.now(), lastLogin: Timestamp.now() } as UserData;
      } else {
        // Update existing user
        await updateDoc(userRef, {
          lastLogin: serverTimestamp(),
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
        });

        return userDoc.data() as UserData;
      }
    }, 'createOrUpdateUser');
  } catch (error) {
    // Try to call the Cloud Function as a fallback
    try {
      const functions = getFunctions();
      const createUserDocumentFn = httpsCallable(functions, 'createUserDocument');

      await createUserDocumentFn({
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
      });

      // Return a basic user data object
      return {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        actionsRemaining: 10,
        isPremium: false,
        createdAt: Timestamp.now(),
        lastLogin: Timestamp.now(),
      } as UserData;
    } catch (fallbackError) {
      throw new Error(
        `Failed to create or update user: ${error}. Fallback also failed: ${fallbackError}`
      );
    }
  }
}

// Get user data
export async function getUserData(userId: string) {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      const userData = userSnap.data();

      // Ensure required fields exist
      if (userData.actionsRemaining === undefined) {
        await updateDoc(userRef, {
          actionsRemaining: 50,
        });
        userData.actionsRemaining = 50;
      }

      if (userData.isPremium === undefined) {
        await updateDoc(userRef, {
          isPremium: false,
        });
        userData.isPremium = false;
      }

      // Ensure user profile data exists
      if (!userData.email && userId) {
        const user = auth.currentUser;
        if (user && user.email) {
          await updateDoc(userRef, {
            email: user.email,
          });
          userData.email = user.email;
        }
      }

      if (!userData.displayName && userId) {
        const user = auth.currentUser;
        if (user && user.displayName) {
          await updateDoc(userRef, {
            displayName: user.displayName,
          });
          userData.displayName = user.displayName;
        }
      }

      return userData;
    }

    // If document doesn't exist, create it with all required fields
    if (userId) {
      const user = auth.currentUser;

      if (user) {
        await createUserDocument(userId, {
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          gameState: {
            elements: initialElements,
            favorites: [],
            discoveries: [],
            goals: [],
            points: 0,
            skipCount: 5,
            zoomLevel: 2,
          },
          createdAt: new Date(),
          lastLoginAt: new Date(),
        });

        // Fetch the newly created document
        const newUserSnap = await getDoc(userRef);
        if (newUserSnap.exists()) {
          return newUserSnap.data();
        }
      }
    }

    return null;
  } catch (error) {
    throw error;
  }
}

export async function createUserDocument(userId: string, initialData: any) {
  const userRef = doc(db, 'users', userId);

  try {
    // Check if document exists first
    const docSnap = await getDoc(userRef);

    if (!docSnap.exists()) {
      // Get current user for profile data if available
      const user = auth.currentUser;

      // Only create if it doesn't exist

      const newUserData = {
        ...initialData,
        uid: userId, // Use uid instead of id to match the UserData interface
        actionsRemaining: 50, // Start with 50 free actions
        isPremium: false,
        email: initialData.email || (user ? user.email : null),
        displayName: initialData.displayName || (user ? user.displayName : null),
        photoURL: initialData.photoURL || (user ? user.photoURL : null),
        createdAt: initialData.createdAt || new Date(),
        lastLoginAt: initialData.lastLoginAt || new Date(),
      };

      await setDoc(userRef, newUserData);

      // Verify the document was created with all required fields
      const verifySnap = await getDoc(userRef);
      if (verifySnap.exists()) {
        const verifyData = verifySnap.data();
      }
    } else {
      // Update last login time if document exists
      await updateDoc(userRef, {
        lastLoginAt: new Date(),
        lastLogin: new Date(), // Update both fields for compatibility
      });

      // Check if required fields exist, add them if not
      const userData = docSnap.data();
      const updates: any = {};
      let needsUpdate = false;

      if (userData.actionsRemaining === undefined) {
        updates.actionsRemaining = 50;
        needsUpdate = true;
      }

      if (userData.isPremium === undefined) {
        updates.isPremium = false;
        needsUpdate = true;
      }

      // Get current user for profile data if available
      const user = auth.currentUser;

      if (!userData.email && user && user.email) {
        updates.email = user.email;
        needsUpdate = true;
      }

      if (!userData.displayName && user && user.displayName) {
        updates.displayName = user.displayName;
        needsUpdate = true;
      }

      if (needsUpdate) {
        await updateDoc(userRef, updates);
      }
    }

    return true;
  } catch (error) {
    throw error;
  }
}

// Helper function to update word relationships
async function updateWordRelationships(
  word: string,
  type: 'input' | 'output' | 'goal',
  forgeType: string,
  relatedWords: { inputs?: string[]; outputs?: string[] }
): Promise<boolean> {
  try {
    // If this is an input word, update its connections to outputs
    if (type === 'input' && relatedWords.outputs && relatedWords.outputs.length > 0) {
      const wordId = word;
      const wordRef = doc(db, 'word_usage', wordId);

      // Get the current document
      const wordDoc = await getDoc(wordRef);
      if (!wordDoc.exists()) {
        return false;
      }

      // Get the current connections or initialize empty object
      const currentData = wordDoc.data();
      const inputConnections = currentData.inputConnections || {};

      // Update connections for each related output
      for (const outputWord of relatedWords.outputs) {
        const normalizedOutput = outputWord.toLowerCase().trim();

        // Increment the count for this output or initialize to 1
        inputConnections[normalizedOutput] = (inputConnections[normalizedOutput] || 0) + 1;
      }

      // Update the document with the new connections
      await updateDoc(wordRef, {
        inputConnections,
      });
    }

    // If this is an output word, update its connections to inputs
    if (type === 'output' && relatedWords.inputs && relatedWords.inputs.length > 0) {
      const wordId = word;
      const wordRef = doc(db, 'word_usage', wordId);

      // Get the current document
      const wordDoc = await getDoc(wordRef);
      if (!wordDoc.exists()) {
        return false;
      }

      // Get the current connections or initialize empty objects
      const currentData = wordDoc.data();
      const outputConnections = currentData.outputConnections || {};
      const deconstructorConnections = currentData.deconstructorConnections || {};

      // Update connections for each related input
      for (const inputWord of relatedWords.inputs) {
        const normalizedInput = inputWord.toLowerCase().trim();

        // Increment the count for this input or initialize to 1
        outputConnections[normalizedInput] = (outputConnections[normalizedInput] || 0) + 1;

        // If this is a deconstructor output, also track in deconstructor connections
        if (forgeType === 'Deconstructor') {
          deconstructorConnections[normalizedInput] =
            (deconstructorConnections[normalizedInput] || 0) + 1;
        }
      }

      // Prepare the update data
      const updateData: any = {
        outputConnections,
      };

      // Only include deconstructor connections if this is a deconstructor output
      if (forgeType === 'Deconstructor') {
        updateData.deconstructorConnections = deconstructorConnections;
      }

      // Update the document with the new connections
      await updateDoc(wordRef, updateData);
    }

    return true;
  } catch (error) {
    return false;
  }
}

// Track word usage for analytics with enhanced relationship tracking
export async function trackWordUsage(
  word: string,
  type: 'input' | 'output' | 'goal',
  forgeType: string,
  userId: string,
  relatedWords?: { inputs?: string[]; outputs?: string[] } // Optional related words for tracking relationships
): Promise<boolean> {
  try {
    // Basic validation
    if (!word) {
      return false;
    }

    if (!userId) {
      return false;
    }

    // Normalize the word (lowercase, trim)
    const normalizedWord = word.toLowerCase().trim();

    // Use just the word as the document ID
    const wordId = normalizedWord;

    try {
      // Get the word usage document
      const wordRef = doc(db, 'word_usage', wordId);

      try {
        const wordDoc = await getDoc(wordRef);

        // Use a transaction to ensure atomic updates

        await runTransaction(db, async (transaction) => {
          if (wordDoc.exists()) {
            // Update existing word usage

            // Prepare the update data
            const updateData: any = {
              count: increment(1),
              timestamp: serverTimestamp(),
              users: arrayUnion(userId),
              forgeType: forgeType, // Always update the forge type
            };

            // Update type-specific count
            if (type === 'input') {
              updateData.inputCount = increment(1);
            } else if (type === 'output') {
              updateData.outputCount = increment(1);

              // If this is a deconstructor output, track it separately
              if (forgeType === 'Deconstructor') {
                updateData.deconstructorCount = increment(1);
              }
            }

            // Make sure we have the connection objects
            const wordData = wordDoc.data();
            if (!wordData.inputConnections) {
              updateData.inputConnections = {};
            }

            if (!wordData.outputConnections) {
              updateData.outputConnections = {};
            }

            if (!wordData.deconstructorConnections) {
              updateData.deconstructorConnections = {};
            }

            // Remove the type field if it exists (we're not using it anymore)
            updateData.type = deleteField();

            // Update the document
            transaction.update(wordRef, updateData);
          } else {
            // Create new word usage with enhanced fields

            const newData: any = {
              word: normalizedWord,
              forgeType,
              count: 1,
              timestamp: serverTimestamp(),
              users: [userId],
              inputCount: type === 'input' ? 1 : 0,
              outputCount: type === 'output' ? 1 : 0,
              deconstructorCount: type === 'output' && forgeType === 'Deconstructor' ? 1 : 0,
              inputConnections: {},
              outputConnections: {},
              deconstructorConnections: {},
            };

            transaction.set(wordRef, newData);
          }
        });
      } catch (docError) {
        throw docError;
      }

      // If we have related words, update the relationships
      if (relatedWords) {
        await updateWordRelationships(normalizedWord, type, forgeType, relatedWords);
      }

      return true;
    } catch (transactionError) {
      // Fallback to direct set/update if transaction fails

      try {
        const wordRef = doc(db, 'word_usage', wordId);

        const wordDoc = await getDoc(wordRef);

        if (wordDoc.exists()) {
          const updateData: any = {
            count: increment(1),
            timestamp: serverTimestamp(),
            users: arrayUnion(userId),
            forgeType: forgeType, // Always update the forge type
          };

          // Update type-specific count
          if (type === 'input') {
            updateData.inputCount = increment(1);
          } else if (type === 'output') {
            updateData.outputCount = increment(1);

            // If this is a deconstructor output, track it separately
            if (forgeType === 'Deconstructor') {
              updateData.deconstructorCount = increment(1);
            }
          }

          // Make sure we have the connection objects
          const wordData = wordDoc.data();
          if (!wordData.inputConnections) {
            updateData.inputConnections = {};
          }

          if (!wordData.outputConnections) {
            updateData.outputConnections = {};
          }

          if (!wordData.deconstructorConnections) {
            updateData.deconstructorConnections = {};
          }

          // Remove the type field if it exists (we're not using it anymore)
          updateData.type = deleteField();

          await updateDoc(wordRef, updateData);
        } else {
          await setDoc(wordRef, {
            word: normalizedWord,
            forgeType,
            count: 1,
            timestamp: serverTimestamp(),
            users: [userId],
            inputCount: type === 'input' ? 1 : 0,
            outputCount: type === 'output' ? 1 : 0,
            deconstructorCount: type === 'output' && forgeType === 'Deconstructor' ? 1 : 0,
            inputConnections: {},
            outputConnections: {},
            deconstructorConnections: {},
          });
        }

        // If we have related words, update the relationships
        if (relatedWords) {
          await updateWordRelationships(normalizedWord, type, forgeType, relatedWords);
        }
      } catch (fallbackError) {
        if (fallbackError instanceof Error) {
        }
        throw fallbackError;
      }

      return true;
    }
  } catch (error) {
    if (error instanceof Error) {
      // Check for permission-denied errors
      if (
        error.message.includes('permission-denied') ||
        error.message.includes('Permission denied')
      ) {
      }
    }

    // Try one last direct approach without transaction
    try {
      const emergencyWordId = word.toLowerCase().trim();
      const wordRef = doc(db, 'word_usage', emergencyWordId);

      // Create a document with all the enhanced fields
      await setDoc(
        wordRef,
        {
          word: word.toLowerCase().trim(),
          forgeType,
          count: 1,
          timestamp: new Date(), // Use JavaScript Date instead of serverTimestamp
          users: [userId],
          inputCount: type === 'input' ? 1 : 0,
          outputCount: type === 'output' ? 1 : 0,
          deconstructorCount: type === 'output' && forgeType === 'Deconstructor' ? 1 : 0,
          inputConnections: {},
          outputConnections: {},
          deconstructorConnections: {},
        },
        { merge: true }
      ); // Use merge option to update if exists

      // If we have related words, try to update relationships one last time
      if (relatedWords) {
        try {
          await updateWordRelationships(word.toLowerCase().trim(), type, forgeType, relatedWords);
        } catch (relationshipError) {
          // Continue anyway since we at least saved the word
        }
      }

      return true;
    } catch (emergencyError) {
      // Now we've truly exhausted all options
      return false;
    }
  }
}

export async function saveGameState(
  userId: string,
  elements: ElementType[],
  favorites: ElementType[],
  discoveries: ElementType[],
  goals: Goal[],
  points: number,
  skipCount: number,
  keyCount: number,
  zoomLevel?: number,
  wordHistory?: {
    Item: string[];
    Location: string[];
    Character: string[];
  },
  stats?: {
    totalDiscoveries: number;
    totalForged: number;
    itemsForged: number;
    locationsForged: number;
    charactersForged: number;
    goalsCompleted: number;
    skipsEarned: number;
    skipsUsed: number;
    elementsDeleted: number;
    keysCollected: number;
    keysUsed: number;
    deconstructions: number;
  },
  combinations?: Record<string, string>,
  deconstructions?: Record<string, boolean>
): Promise<boolean> {
  const userRef = doc(db, 'users', userId);

  try {
    // Check if document exists first
    const docSnap = await getDoc(userRef);

    if (!docSnap.exists()) {
      // Create document if it doesn't exist
      await setDoc(userRef, {
        id: userId,
        gameState: {
          elements,
          favorites,
          discoveries,
          goals,
          points,
          skipCount,
          keyCount,
          zoomLevel,
          wordHistory: wordHistory || {
            Item: [],
            Location: [],
            Character: [],
          },
          combinations: combinations || {},
          deconstructions: deconstructions || {},
          stats: stats || {
            totalDiscoveries: 0,
            totalForged: 0,
            itemsForged: 0,
            locationsForged: 0,
            charactersForged: 0,
            goalsCompleted: 0,
            skipsEarned: 0,
            skipsUsed: 0,
            elementsDeleted: 0,
            keysCollected: 0,
            keysUsed: 0,
            deconstructions: 0,
          },
        },
        createdAt: new Date(),
        lastUpdatedAt: new Date(),
      });
    } else {
      // Update existing document
      const updateData: any = {
        'gameState.elements': elements,
        'gameState.favorites': favorites,
        'gameState.discoveries': discoveries,
        'gameState.goals': goals,
        'gameState.points': points,
        'gameState.skipCount': skipCount,
        'gameState.keyCount': keyCount,
        'gameState.zoomLevel': zoomLevel,
        lastUpdatedAt: new Date(),
      };

      // Only update wordHistory if it's provided
      if (wordHistory) {
        updateData['gameState.wordHistory'] = wordHistory;
      }

      // Only update stats if it's provided
      if (stats) {
        // Ensure stats are numbers, not undefined or NaN
        const sanitizedStats = {
          ...stats,
          keysCollected:
            typeof stats.keysCollected === 'number' && !isNaN(stats.keysCollected)
              ? stats.keysCollected
              : 0,
          keysUsed:
            typeof stats.keysUsed === 'number' && !isNaN(stats.keysUsed) ? stats.keysUsed : 0,
          deconstructions:
            typeof stats.deconstructions === 'number' && !isNaN(stats.deconstructions)
              ? stats.deconstructions
              : 0,
        };
        updateData['gameState.stats'] = sanitizedStats;
      }

      // Only update combinations if it's provided
      if (combinations) {
        updateData['gameState.combinations'] = combinations;
      }

      // Only update deconstructions if it's provided
      if (deconstructions) {
        updateData['gameState.deconstructions'] = deconstructions;
      }

      await updateDoc(userRef, updateData);
    }

    return true;
  } catch (error) {
    throw error;
  }
}

// Use an action
export async function useAction(userId: string): Promise<boolean> {
  try {
    return await retryFirestoreOperation(async () => {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        // Create the user document first with default values

        await setDoc(userRef, {
          uid: userId,
          actionsRemaining: 49, // Start with 50 but use one immediately
          isPremium: false,
          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp(),
        });
        return true;
      }

      const userData = userDoc.data() as UserData;

      if (userData.actionsRemaining <= 0) {
        return false;
      }

      await updateDoc(userRef, {
        actionsRemaining: increment(-1),
      });

      return true;
    }, 'useAction');
  } catch (error) {
    return false;
  }
}

// Add actions to user account
export async function addActions(
  userId: string,
  actionsToAdd: number,
  paymentId?: string,
  amount?: number
): Promise<boolean> {
  try {
    return await retryFirestoreOperation(async () => {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        // Create the user document first with default values

        await setDoc(userRef, {
          uid: userId,
          actionsRemaining: actionsToAdd,
          isPremium: paymentId ? true : false,
          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp(),
          purchases:
            paymentId && amount
              ? [
                  {
                    date: new Date(), // Use JavaScript Date instead of serverTimestamp
                    amount: amount,
                    actionsAdded: actionsToAdd,
                    paymentId: paymentId,
                  },
                ]
              : [],
        });
      } else {
        // Update existing document
        if (paymentId && amount) {
          // First update the actions and premium status
          await updateDoc(userRef, {
            actionsRemaining: increment(actionsToAdd),
            isPremium: true,
          });

          // Then add the purchase record separately
          // Create a purchase object with a JavaScript Date
          const purchase = {
            date: new Date(), // Use JavaScript Date instead of serverTimestamp
            amount: amount,
            actionsAdded: actionsToAdd,
            paymentId: paymentId,
          };

          // Get the current purchases array
          const userData = userDoc.data();
          const currentPurchases = userData?.purchases || [];

          // Update with the new array
          await updateDoc(userRef, {
            purchases: [...currentPurchases, purchase],
          });
        } else {
          // Just add actions without recording a purchase
          await updateDoc(userRef, {
            actionsRemaining: increment(actionsToAdd),
          });
        }
      }

      return true;
    }, 'addActions');
  } catch (error) {
    return false;
  }
}

// Anonymous user tracking has been removed
